import React, { useCallback, useEffect, useMemo } from "react";
import { ActionButton } from "@culturehq/components";

import { useProfile, useRecognitionTypes } from "app/context";
import { Trans } from "app/utils";

import { RecognitionTypeMediumImage } from "components/recognitions/RecognitionTypeImage";

import styles from "./styles.module.scss";

const makeUserList = users => {
  if (users.length === 1) {
    return users[0].name;
  }

  if (users.length === 2) {
    return `${users[0].name} and ${users[1].name}`;
  }

  const names = users.map(({ name }) => name);

  if (users.length < 5) {
    return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
  }

  return `${names.slice(0, 4).join(", ")}, and ${users.length - 4} more`;
};

const RecognizeRecognitionType = ({ onTypeSelected, recognitionType }) => {
  const onClick = useCallback(
    () => onTypeSelected(recognitionType),
    [onTypeSelected, recognitionType]
  );

  return (
    <div className={styles.type} onClick={onClick}>
      {recognitionType.name}
      <br />
      <RecognitionTypeMediumImage recognitionType={recognitionType} />
    </div>
  );
};

const getRecognitionTypes = (recognitionTypes, profile) => {
  let list = Object.keys(recognitionTypes).reduce(
    (accum, recognitionTypeId) => [...accum, recognitionTypes[recognitionTypeId]], []
  );

  if (!profile.organizationAdmin) {
    list = list.filter(({ approval }) => approval !== "admin_only");
  }

  return list.sort((a, b) => a.name.localeCompare(b.name));
};

const RecognizeSelectType = ({ onPrevClicked, onTypeSelected, users }) => {
  const { recognitionTypes, onAllRecognitionTypesRequested } = useRecognitionTypes();
  const { profile } = useProfile();

  const names = useMemo(() => makeUserList(users), [users]);
  const recognitionTypeList = useMemo(
    () => getRecognitionTypes(recognitionTypes, profile),
    [recognitionTypes, profile]
  );

  useEffect(onAllRecognitionTypesRequested, []);

  return (
    <>
      <ActionButton className={styles.prev} onClick={onPrevClicked}>
        <Trans>← Go back</Trans>
      </ActionButton>
      <h1>
        <Trans>Recognizing {names}. Which type of recognition would you like to give?</Trans>
      </h1>
      <div className={styles.types}>
        {recognitionTypeList.map(recognitionType => (
          <RecognizeRecognitionType
            key={recognitionType.id}
            recognitionType={recognitionType}
            onTypeSelected={onTypeSelected}
          />
        ))}
      </div>
      <p className={styles.help}>
        <Trans>Next: Add a description and personalize your recognition</Trans>
      </p>
    </>
  );
};

export default RecognizeSelectType;
